<script setup lang="ts">
/*
 * Payroll schedule card matching the prototype's .schedule-card variants:
 *  - default    → 280px wide, 1px app-border, 16px radius
 *  - active     → primary border + 1px primary inset shadow
 *  - locked     → dashed border, 60% opacity, lock badge
 *  - off-cycle  → left violet stripe, faint violet tint
 *
 * Used on the Pay center landing page in Upcoming / In-Progress / Off-cycle /
 * Completed sections.
 */
import { computed } from 'vue';
import { Lock } from 'lucide-vue-next';

type State = 'default' | 'active' | 'locked';

const props = withDefaults(
  defineProps<{
    state?: State;
    offCycle?: boolean;
    title: string;
    description?: string;
  }>(),
  { state: 'default' },
);

defineEmits<{ click: [] }>();

const stateClass = computed(() => {
  if (props.state === 'locked') {
    return 'border-dashed bg-slate-50 cursor-not-allowed';
  }
  if (props.state === 'active') {
    return 'border-primary bg-[#F6F7FF] ring-1 ring-primary cursor-pointer';
  }
  return 'cursor-pointer hover:border-primary-200 hover:shadow-card-hover';
});
</script>

<template>
  <button
    type="button"
    :class="[
      'schedule-card relative flex w-[280px] shrink-0 flex-col gap-1.5 rounded-3xl border border-app-border bg-app-surface p-4 text-left transition-colors',
      offCycle ? 'schedule-off-cycle' : '',
      stateClass,
      state === 'locked' ? 'pointer-events-none' : '',
    ]"
    :aria-disabled="state === 'locked' || undefined"
    :aria-current="state === 'active' ? 'true' : undefined"
    @click="state !== 'locked' && $emit('click')"
  >
    <span
      v-if="state === 'locked'"
      class="absolute right-3 top-3 inline-flex items-center gap-1 rounded-pill border border-app-border bg-app-surface px-2 py-0.5 text-[10px] font-medium text-slate-500"
    >
      <Lock class="h-3 w-3" aria-hidden="true" /> Locked
    </span>
    <h4 :class="['text-base font-semibold text-app-fg', state === 'locked' ? 'opacity-60' : '']">
      {{ title }}
    </h4>
    <p
      v-if="description"
      :class="['sc-desc text-md text-app-muted', state === 'locked' ? 'opacity-60' : '']"
      style="
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      "
    >
      {{ description }}
    </p>
    <div :class="['flex items-center gap-2', state === 'locked' ? 'opacity-60' : '']">
      <slot />
    </div>
  </button>
</template>

<style scoped>
.schedule-off-cycle {
  background: linear-gradient(to right, #faf5ff 0px, #faf5ff 4px, var(--color-app-surface) 4px);
}
.schedule-off-cycle[aria-current='true'] {
  background: linear-gradient(
    to right,
    var(--color-offcycle-stripe) 0px,
    var(--color-offcycle-stripe) 4px,
    #f6f7ff 4px
  );
}
</style>
